// optionMatching — snap an incoming value onto a field's CONFIGURED option.
//
// Any value that arrives from outside the form (an AI parse of a JD or resume,
// an edit prefill, a cross-module prefill, an import) is free text. A select /
// radio / checkbox control only selects when the value is character-for-
// character one of its configured option values, so "onsite", "ONSITE",
// "On Site" and "on_site" all silently failed to select the "On-Site" radio —
// the parse looked like it had worked while the control sat empty.
//
// Matching is deliberately CONSERVATIVE and lossless:
//   • only fields that actually declare options are touched;
//   • an unmatched value is returned UNCHANGED, never blanked — a value we
//     cannot map is still shown to the user (and still saved) rather than
//     silently dropped;
//   • matching never invents a selection: it compares against the option's own
//     value and label only, plus whatever aliases the admin configured.
//
// Everything here is config-driven; no module, field or option name appears.

// canonical — the comparison key. Case-folded, accent-folded and stripped of
// every non-alphanumeric character, so "On-Site" / "on site" / "ON_SITE" /
// "onsite" all collapse onto "onsite". Digits are kept so "1" ≠ "10".
export function canonical(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

// optionEntries — the (canonicalKey → optionValue) pairs a field offers.
// Both the option's value and its label are accepted as input, because a parse
// far more often echoes the human label ("Full Time") than the stored value.
// field.optionAliases lets an admin add synonyms the data uses but the option
// list does not spell out, e.g. { "WFH": "Remote", "work from home": "Remote" }.
export function optionEntries(field) {
  const pairs = new Map();
  const add = (key, target) => {
    const k = canonical(key);
    // First writer wins: an earlier option keeps the key when a later option's
    // label happens to collapse onto the same string.
    if (k && !pairs.has(k)) pairs.set(k, target);
  };

  (Array.isArray(field?.options) ? field.options : []).forEach((option) => {
    if (option === null || option === undefined) return;
    const value = typeof option === 'object' ? option.value : option;
    if (value === undefined || value === null || value === '') return;
    add(value, value);
    if (typeof option === 'object' && option.label !== undefined) add(option.label, value);
  });

  Object.entries(field?.optionAliases ?? {}).forEach(([alias, target]) => {
    // An alias may only point AT a real option — otherwise a typo in config
    // would inject a value the control cannot select, which is the exact class
    // of bug this module exists to remove.
    const resolved = pairs.get(canonical(target));
    if (resolved !== undefined) add(alias, resolved);
  });

  return pairs;
}

const hasOptions = (field) => Array.isArray(field?.options) && field.options.length > 0;

// snapOne — map a single scalar onto an option value, or return it unchanged.
function snapOne(pairs, value) {
  if (value === undefined || value === null || value === '') return value;
  // An object (a resolved reference like { id, value }) is not free text — the
  // reference resolver already owns it, so leave it entirely alone.
  if (typeof value === 'object') return value;
  const match = pairs.get(canonical(value));
  return match === undefined ? value : match;
}

// snapToOption — the entry point. Arrays map element-wise so a multi-select or
// a checkbox group snaps every entry. Returns the input untouched for fields
// with no options, which is most of them.
export function snapToOption(field, value) {
  if (!hasOptions(field)) return value;
  const pairs = optionEntries(field);
  if (pairs.size === 0) return value;
  if (Array.isArray(value)) return value.map((item) => snapOne(pairs, item));
  return snapOne(pairs, value);
}

// unmatchedOptionValues — the entries that could NOT be mapped onto an option.
// Callers use it to tell the user what a parse failed to place, instead of
// leaving a control looking mysteriously empty.
export function unmatchedOptionValues(field, value) {
  if (!hasOptions(field)) return [];
  const pairs = optionEntries(field);
  const list = Array.isArray(value) ? value : [value];
  return list.filter((item) => item !== undefined && item !== null && item !== ''
    && typeof item !== 'object'
    && !pairs.has(canonical(item)));
}
